import React, { useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { ReactComponent as IconEdit } from "../../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconCheck } from "../../../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../../../shared/assets/Icons/IconCrossSquare.svg";
import { ReactComponent as IconPlus } from "../../../../../../shared/assets/Icons/IconPlusNV.svg";
import {
  InternetP2PAddressing,
  P2PLeg,
  P2PLegRole,
  P2P_LEG_LABELS,
  P2P_LEG_ROLES,
  emptyP2PLeg,
  isP2PLegEmpty,
  normalizeMask,
  p2pLegOf,
  withP2PLeg,
} from "../internetAddressing";

/**
 * The P2P /31 of each leg of an Internet: at most a primary and a secondary,
 * one row each (CTX-1132). The table used to let any number of rows be added
 * and then kept only the first, so a second row looked saved and was not.
 */

interface P2PAddressingRow extends P2PLeg {
  role: P2PLegRole;
}

type LegField = keyof P2PLeg;

interface EditingLeg {
  role: P2PLegRole;
  draft: P2PLeg;
  // A leg added from "Add" disappears on Cancel; an existing one is restored.
  isNew: boolean;
}

export interface P2PAddressingTableProps {
  p2pAddressing: InternetP2PAddressing;
  onUpdate: (p2pAddressing: InternetP2PAddressing) => void;
}

const FIELDS: Array<{ field: LegField; header: string }> = [
  { field: "network", header: "Network" },
  { field: "gateway", header: "Gateway" },
  { field: "peerIp", header: "Peer IP" },
  { field: "mask", header: "Mask" },
];

const displayValue = (value: string | number | null): string =>
  value === null || String(value).trim() === "" ? "-" : String(value);

export const P2PAddressingTable: React.FC<P2PAddressingTableProps> = ({
  p2pAddressing,
  onUpdate,
}) => {
  const [editing, setEditing] = useState<EditingLeg | null>(null);

  const savedRows: P2PAddressingRow[] = P2P_LEG_ROLES.flatMap((role) => {
    const leg = p2pLegOf(p2pAddressing, role);
    return leg ? [{ role, ...leg }] : [];
  });
  const rows: P2PAddressingRow[] =
    editing?.isNew === true
      ? [...savedRows, { role: editing.role, ...editing.draft }]
      : savedRows;

  // The secondary only exists next to a primary, so the next free leg is the
  // first missing one in order, and nothing once both are there.
  const nextRole = P2P_LEG_ROLES.find(
    (role) => !rows.some((row) => row.role === role)
  );
  const hasSecondary = rows.some((row) => row.role === "secondary");

  const startEditing = (role: P2PLegRole) => {
    const leg = p2pLegOf(p2pAddressing, role);
    setEditing({ role, draft: leg ?? { ...emptyP2PLeg }, isNew: leg === null });
  };

  const changeDraft = (field: LegField, value: string) => {
    setEditing((current) =>
      current ? { ...current, draft: { ...current.draft, [field]: value } } : current
    );
  };

  const saveDraft = () => {
    if (!editing) return;
    // Saving a leg with every field cleared is the same as removing it.
    const leg = isP2PLegEmpty(editing.draft)
      ? null
      : { ...editing.draft, mask: normalizeMask(editing.draft.mask) };
    onUpdate(withP2PLeg(p2pAddressing, editing.role, leg));
    setEditing(null);
  };

  const removeLeg = (role: P2PLegRole) => {
    onUpdate(withP2PLeg(p2pAddressing, role, null));
    if (editing?.role === role) setEditing(null);
  };

  const fieldColumns: GenericTableColumn<P2PAddressingRow>[] = FIELDS.map(
    ({ field, header }) => ({
      header,
      accessor: field,
      renderCell: (row) =>
        editing?.role === row.role ? (
          <GenericInput
            label=""
            value={editing.draft[field] === null ? "" : String(editing.draft[field])}
            onChange={(value) => changeDraft(field, value)}
            placeholder={header}
          />
        ) : (
          <span className="text-sm text-semantic-text-secondary">
            {displayValue(row[field])}
          </span>
        ),
    })
  );

  const columns: GenericTableColumn<P2PAddressingRow>[] = [
    {
      header: "Leg",
      renderCell: (row) => (
        <span className="text-sm font-medium text-semantic-text-primary">
          {P2P_LEG_LABELS[row.role]}
        </span>
      ),
    },
    ...fieldColumns,
    {
      header: "Actions",
      renderCell: (row) => {
        if (editing?.role === row.role) {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={saveDraft}
                className="flex items-center justify-center w-8 h-8 border border-semantic-border-secondary rounded text-green-600 hover:bg-green-50"
                title="Save"
              >
                <IconCheck className="w-5 h-5" />
              </button>
              <button
                onClick={() => setEditing(null)}
                className="flex items-center justify-center w-8 h-8 border border-semantic-border-secondary rounded text-red-600 hover:bg-red-50"
                title="Cancel"
              >
                <IconCancel className="w-5 h-5" />
              </button>
            </div>
          );
        }
        // Removing the primary would leave a secondary with nothing to back.
        const isDeleteBlocked = row.role === "primary" && hasSecondary;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => startEditing(row.role)}
              disabled={editing !== null}
              className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              disabled:opacity-50
              w-10 h-10
            "
              title="Edit"
            >
              <IconEdit className="w-10 h-10" />
            </button>
            <button
              onClick={() => removeLeg(row.role)}
              disabled={isDeleteBlocked || editing !== null}
              className="
              flex items-center justify-center
              p-2
              border border-semantic-border-secondary
              rounded
              bg-red-50
              text-red-600
              hover:bg-red-100
              disabled:opacity-50
              w-10 h-10"
              title={isDeleteBlocked ? "Remove the secondary leg first" : "Delete"}
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <GenericCard isCollapsible={false} className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold text-semantic-text-primary">
          P2P Addressing
        </h3>
        <GenericButton
          label={
            <div className="flex items-center gap-2">
              <IconPlus className="w-5 h-5" />
              <span>{nextRole ? `Add ${P2P_LEG_LABELS[nextRole].toLowerCase()}` : "Add"}</span>
            </div>
          }
          variant="secondary"
          size="sm"
          onClick={() => nextRole && startEditing(nextRole)}
          disabled={nextRole === undefined || editing !== null}
        />
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-8 text-semantic-text-secondary">
          No P2P addressing entries. Click &quot;Add primary&quot; to add one.
        </div>
      ) : (
        <GenericTable
          columns={columns}
          data={rows}
          rowKey={(row) => row.role}
          tableClassName="bg-semantic-bg-primary"
          width="100%"
          isFooterVisible={false}
          initialRowsPerPage={10}
          rowsPerPageOptions={[5, 10, 20]}
        />
      )}
    </GenericCard>
  );
};
